import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Spinner,
  Center,
  Box,
  Icon,
} from '@chakra-ui/react';
import { MdEdit, MdOutlineDelete } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as api from '../../../../api/evento'

interface Evento {
  seqConteudo: number;
  nomePublicacao: string;
  datRegistro: string;
  datPublicacao: string;
  datDespublicar: string;
  dscSituacaoPublicacao: string;
}

interface TabelaEventoProps {
  listaEvento: Evento[];
  listaVazia: string;
  loading: boolean;
  onDelete: (seqEvento: number) => void;
}

type Ordenacao = { chave: keyof Evento; direcao: 'asc' | 'desc' } | null;

const TabelaEvento = ({ 
  listaEvento, 
  listaVazia, 
  loading,
  onDelete
}: TabelaEventoProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Evento | null>(null);

  function deletarPublicacao(seqPublicacao: number) {
    Swal.fire({
      title: 'Tem certeza?',
      text: "Certeza que deseja deletar esta publicação?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1789FC',
      cancelButtonColor: '#EDF2F7',
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'info',
          title: 'Aguarde',
          allowOutsideClick: false,
          text: 'Deletando...',
          showConfirmButton: false,
      });
          try {
            const obterEvento = await api.obterEvento(seqPublicacao);
            if(obterEvento.binPublicacao) {
              const delimiter = "/cms-dev-files/";
              const index = obterEvento.binPublicacao.lastIndexOf(delimiter);
              const stringFinalDaUrlCdn =
                index !== -1 ? obterEvento.binPublicacao.substring(index + delimiter.length) : obterEvento.binPublicacao;
              await api.deletarEventoNoCdn(stringFinalDaUrlCdn);
            }
          } catch (error) {
            console.error("Erro ao obter publicação:", error);
          }
          try {
          await api.deletarEvento(seqPublicacao).then(() => {
            onDelete(seqPublicacao);
            Swal.fire({
              icon: 'success',
              title: 'Sucesso!',
              text: 'Publicação deletada com sucesso.',
              confirmButtonColor: '#1789FC',
              confirmButtonText: 'OK',
            })
          });
        } catch (error) {
          console.error('Erro ao deletar publicação:', error);
          Swal.fire({
            icon: 'error',
            title: 'Ocorreu um erro!',
            text: 'Erro ao deletar publicação. Tente novamente mais tarde.',
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate(-1)
          });
        }
      }
    });
  }

  const manipularOrdenacao = (chave: keyof Evento) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaEvento];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaEvento, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Evento) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const limitarTexto = (texto: string, tamanhoMaximo: number) => {
    return texto.length > tamanhoMaximo ? texto.substring(0, tamanhoMaximo) + '...' : texto;
  };

  const renderizarLinhas = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((evento) => (
        <Tr key={evento.seqConteudo}>
          <Td>{limitarTexto(evento.nomePublicacao, 30)}</Td>
          <Td>{evento.dscSituacaoPublicacao}</Td>
          <Td>{evento.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{evento.datPublicacao.split('-').reverse().join('/')}</Td>
          <Td>{evento.datDespublicar.split('-').reverse().join('/')}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-evento/${evento.seqConteudo}`)}
            >
              <MdEdit fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
          <Td>
            <IconButton
              aria-label="Botão deletar"
              bgColor="#fff"
              _hover={{ bg: '#ff00002b' }}
              onClick={() => deletarPublicacao(evento.seqConteudo)}
            >
              <Icon as={MdOutlineDelete} fontSize="24px" color="red"/>
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={6} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('nomePublicacao')}
              onMouseEnter={() => setColunaDestacada('nomePublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'nomePublicacao' ? 'blue.500' : undefined}
            >
              <Box display="flex" alignItems="center">
                Título {obterIndicadorOrdenacao('nomePublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscSituacaoPublicacao')}
              onMouseEnter={() => setColunaDestacada('dscSituacaoPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'dscSituacaoPublicacao' ? 'blue.500' : undefined}
            >
              <Box display="flex" alignItems="center">
                Situação {obterIndicadorOrdenacao('dscSituacaoPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'datRegistro' ? 'blue.500' : undefined}
            >
              <Box display="flex" alignItems="center">
                Data do Registro {obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datPublicacao')}
              onMouseEnter={() => setColunaDestacada('datPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'datPublicacao' ? 'blue.500' : undefined}
            >
              <Box display="flex" alignItems="center">
                Data para Publicação {obterIndicadorOrdenacao('datPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datDespublicar')}
              onMouseEnter={() => setColunaDestacada('datDespublicar')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'datDespublicar' ? 'blue.500' : undefined}
            >
              <Box display="flex" alignItems="center">
                Data Fim da Publicação {obterIndicadorOrdenacao('datDespublicar')}
              </Box>
            </Th>
            <Th>Editar</Th>
            <Th>Deletar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={6}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderizarLinhas()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaEvento;
